/**
 * Live-availability registry for the four features that still call the mock-only `/api/v1`
 * client (`./client.ts`) — see that file's own header for the full list and why it wasn't
 * deleted. `/api/v1` is not served on the live Frappe bench at all (confirmed: a call there
 * answers `DoesNotExistError`), so once `vite.config.ts` routes `/api` wholesale to live (see its
 * header), every one of these four would fire a request that 404s.
 *
 * Rather than let each screen find that out at request time, each feature disables its OWN
 * control client-side and says why — "Not available yet.", never a developer-facing string like
 * "no /api/v1 endpoint on live". This file is the ONE place that decision lives: flipping an
 * entry to `true` the moment its endpoint ships is a one-line change here, not a hunt through
 * four components for a scattered boolean.
 *
 * The specific ask that unblocks each feature is numbered A3/A4/A6/A8 in
 * `docs/bosun-required-api-round-2.md` §A — same letters, so a reader can jump straight from a
 * disabled control to the exact backend request that turns it back on.
 *
 * `enquirySources` (A7) USED to live here, gating `features/rfq/SourceCard.tsx`'s call to the
 * mock-only `/enquiries/:id/sources`. It's gone: `features/rfq/SourcePanel.tsx` replaces that
 * whole panel with a real path — `enquiry.mail_id` (now on the wire, see `useEnquiries.ts`'s
 * `toEnquiryHeader`) plus `mail.get_email`, both live methods already used elsewhere in this
 * codebase. There is nothing left for this file to gate on that path.
 */

export type LiveFeature =
  | 'quotedItems'
  | 'mailAttachmentUpload'
  | 'enquiryExtractUpload'
  | 'supplierCreate'

/**
 * `false` means "still `/api/v1`-only, disable the control." Every entry is `false` today — flip
 * one to `true` (and move its hook off `./client.ts` onto a real `quoflow.*` method) once the
 * matching ask below ships, and every caller re-enables itself automatically.
 */
export const LIVE_FEATURE_AVAILABILITY: Record<LiveFeature, boolean> = {

  // A8 — `quoflow.quotation.get_quoted_items`, taking a `Customer` docname and returning past
  // quoted lines. Reads `GET /customers/:id/quoted-items`.
  quotedItems: false,
  // A4 — SHIPPED, 2026-09-09. `mail.upload_attachment` takes `{file_name, file_data (base64),
  // is_private}` and returns `{file_url, file_name}` — probed live, and wired up in
  // `lib/mailUpload.ts`. It existed the whole time under a name nothing here had tried; the ask
  // was for a method that already answered. Uploads are always PRIVATE (Akhil): a public one
  // lands at `/files/…`, readable by anyone who reaches the origin, and these are customer
  // documents.
  mailAttachmentUpload: true,
  // A3 — WITHDRAWN, 2026-09-09. This asked for a server method to extract items from an uploaded
  // file, and it turned out not to be needed: `lib/readUploadedFile.ts` opens xlsx/xlsm, legacy
  // .xls, .doc and PDF in the BROWSER, with the same parsers that already read mail attachments.
  // The server-side pipeline it would have used (`mail.extract_enquiry_items`) is dead anyway
  // (`B1`), so waiting on it kept a working feature switched off.
  enquiryExtractUpload: true,
  // A6 — SHIPPED, 2026-09-03. `sales_person.create_supplier` takes `{supplier_name, email}` and
  // returns the new docname; probed live and wired up in `createSupplier.ts`. The remaining gap is
  // narrower than "no endpoint": it DISCARDS the email (verified by reading the created doc back),
  // so `NewSupplierBar` still says the address must be added in the supplier master. Tracked as
  // the residue of `E6` in `docs/bosun-frappe-asks.md`.
  supplierCreate: true,
}

/** User-facing copy for a disabled control or an empty read panel. Matches the idiom
 * `EnquiryHeaderCard.tsx` already established for fields with no live read/write path — plain
 * language a purchaser or salesperson recognises, not an internal transport detail. */
export const NOT_AVAILABLE_YET = 'Not available yet.'

/** Whether `feature` has a live endpoint yet. Pure — safe to call from render. */
export function isLiveFeature(feature: LiveFeature): boolean {
  return LIVE_FEATURE_AVAILABILITY[feature]
}
